// src/page/CouponEdit.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { TICKET_GENRE_OPTIONS } from '../constants/ticketOptions';
import { AREA_OPTIONS, GENDER_OPTIONS, AGE_OPTIONS } from '../constants/spotOptions';

export default function CouponEdit() {
  useAuthGuard();
  const navigate = useNavigate();
  const { packageID } = useParams();

  // ---------- メッセージ ----------
  const MSG = {
    errPackageName: 'クーポン名は必須です',
    errSpot: 'スポットは必須です',
    errPublish: '公開設定を選択してください',
    errSales: '有効期間は必須です',
    errPublishRange: '公開期間は必須です',
    errVoucherUsage: 'チケット利用区分を選択してください',
    errNumberOfUsePerUser: '1人利用可能回数は0以上の数値で必須です',
    errUseLimitByDay: '1日の使用上限回数は0以上の数値で必須です',
    saved: '更新しました',
    deleted: '削除しました',
    apiError: 'サーバーエラーが発生しました',
    imgSaved: '画像を更新しました',
    imgDeleted: '画像を削除しました'
  };

  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) =>
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtRange = (a, b) => (a && b ? `${fmtDate(a)} ~ ${fmtDate(b)}` : '');

  // "YYYY/MM/DD HH:mm" -> ISO (UTC, RFC3339)
  const parseLocalToISO = (s) => {
    if (!s) return null;
    const m = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\s+(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const [, yStr, moStr, dStr, hStr, miStr] = m;
    const y = Number(yStr), mo = Number(moStr), d = Number(dStr), h = Number(hStr), mi = Number(miStr);
    const dt = new Date(y, mo - 1, d, h, mi, 0);
    return dt.toISOString();
  };

  const parseRangeToISO = (rangeText) => {
    if (!rangeText) return { start: null, end: null };
    const [a, b] = rangeText.split(/~|〜|から|-/).map((s) => s.trim());
    return { start: parseLocalToISO(a), end: parseLocalToISO(b) };
  };

  // ---- file URL helper (public serving path) ----
  const fileURL = (p) => (p ? `${API_BASE_URL.replace(/\/$/, '')}/files/${encodeURIComponent(p)}` : '');
  const isBlobUrl = (u) => {
    try { return new URL(u).protocol === 'blob:'; } catch { return false; }
  };

  // ---------- states ----------
  const [packageName, setCouponName] = useState('');
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);

  const [displayOrder, setDisplayOrder] = useState('');
  const [publishedFlag, setPublishedFlag] = useState(null); // true/false/null

  const [howToUse, setHowToUse] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [caution, setCaution] = useState('');

  const [ticketArea, setTicketArea] = useState('');
  const [genres, setGenres] = useState([]); // CSV -> array

  const [salesRange, setSalesRange] = useState('');
  const [publishRange, setPublishRange] = useState('');

  const [couponLevel, setCouponLevel] = useState('');
  const [usePoint, setUsePoint] = useState('');
  const [maxNumber, setMaxNumber] = useState('');

  // voucher editable
  const [maxPerUser, setMaxPerUser] = useState(''); // numberOfUsePerUser
  const [useLimitByDay, setUseLimitByDay] = useState('');
  const [voucherUsage, setVoucherUsage] = useState(''); // '0' | '1'

  // 画像：SpotEdit と同じ挙動
  const [serverImagePath, setServerImagePath] = useState(''); // persisted path (couponImage or imagePath)
  const [imageFile, setImageFile] = useState(null);           // File | null
  const [imagePreview, setImagePreview] = useState('');       // blob URL or server URL
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  // validation
  const [errors, setErrors] = useState({});

  // recommend_attributes (optional)
  const [genders, setGenders] = useState([]); // ["2"]
  const [ages, setAges] = useState([]);       // ["0-20","20-30",...]

  // pickers
  const salesPickerRef = useRef(null);
  const publishPickerRef = useRef(null);

  // input refs
  const salesInputRef = useRef(null);
  const publishInputRef = useRef(null);

  // submit/delete/upload state
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const savingBusy = submitting || deleting || uploading;

  // --- flatpickr init ---
  useEffect(() => {
    if (!window.flatpickr) return;
    const ja = window.flatpickr.l10ns?.ja || {};

    const salesNode = document.getElementById('sales_range');
    const publishNode = document.getElementById('publish_range');

    if (salesNode) {
      salesPickerRef.current = window.flatpickr(salesNode, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selected) => {
          if (selected?.length === 2) {
            setSalesRange(`${fmtDate(selected[0])} ~ ${fmtDate(selected[1])}`);
          } else if (!selected?.length) {
            setSalesRange('');
          }
        }
      });
    }
    if (publishNode) {
      publishPickerRef.current = window.flatpickr(publishNode, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selected) => {
          if (selected?.length === 2) {
            setPublishRange(`${fmtDate(selected[0])} ~ ${fmtDate(selected[1])}`);
          } else if (!selected?.length) {
            setPublishRange('');
          }
        }
      });
    }

    return () => {
      try { salesPickerRef.current?.destroy(); } catch {}
      try { publishPickerRef.current?.destroy(); } catch {}
    };
  }, []);

  // reflect state -> pickers
  useEffect(() => {
    if (!salesPickerRef.current) return;
    if (salesRange && salesRange.includes('~')) {
      const [a, b] = salesRange.split('~').map(s => s.trim());
      salesPickerRef.current.setDate([a, b], true);
    } else {
      try { salesPickerRef.current.clear(); } catch {}
    }
  }, [salesRange]);

  useEffect(() => {
    if (!publishPickerRef.current) return;
    if (publishRange && publishRange.includes('~')) {
      const [a, b] = publishRange.split('~').map(s => s.trim());
      publishPickerRef.current.setDate([a, b], true);
    } else {
      try { publishPickerRef.current.clear(); } catch {}
    }
  }, [publishRange]);

  // spots
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch {
        setSpots([]);
      } finally {
        setLoadingSpots(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // 画像プレビューの開放
  useEffect(() => () => {
    if (imagePreview && isBlobUrl(imagePreview)) {
      try { URL.revokeObjectURL(imagePreview); } catch {}
    }
  }, [imagePreview]);

  // coupon + voucher detail → プリセット
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setApiError('');
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/coupon/${packageID}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const p = data?.coupon || {};
        const v = data?.voucher || {};

        // package
        setCouponName(p.packageName || '');
        setSpotId(p.spotId || '');
        setDisplayOrder(p.displayOrder ?? '');

        if (typeof p.publishedFlag === 'boolean') setPublishedFlag(p.publishedFlag);

        setHowToUse(p.howToUse ?? '');
        setTermsOfUse(p.termsOfUse ?? '');
        setCaution(p.caution ?? '');

        // 画像（couponImage or imagePath）をプリセット
        const persistedPath = p.couponImage || p.imagePath || '';
        setServerImagePath(persistedPath || '');
        setImagePreview(persistedPath ? fileURL(persistedPath) : '');

        const gRaw = p.couponGenre;
        const gList = typeof gRaw === 'string'
          ? gRaw.split(',').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(gRaw) ? gRaw : [];
        setGenres(gList.map(String));

        setCouponLevel(p.couponLevel == null ? '' : String(p.couponLevel));
        setUsePoint(p.usePoint ?? '');
        setMaxNumber(p.maxNumber ?? '');

        // ranges
        const saleA = p.salesStartDate ? new Date(p.salesStartDate) : null;
        const saleB = p.salesEndDate ? new Date(p.salesEndDate) : null;
        const pubA = p.publishedStartDate ? new Date(p.publishedStartDate) : null;
        const pubB = p.publishedEndDate ? new Date(p.publishedEndDate) : null;

        setSalesRange(saleA && saleB ? fmtRange(saleA, saleB) : '');
        setPublishRange(pubA && pubB ? fmtRange(pubA, pubB) : '');

        // recommend_attributes
        const ra = p.recommendAttributes ?? p.recommend_attributes;
        let sexTypes = [];
        let ageGroups = [];
        try {
          if (typeof ra === 'string') {
            const obj = JSON.parse(ra);
            sexTypes = Array.isArray(obj?.sexTypes) ? obj.sexTypes : [];
            ageGroups = Array.isArray(obj?.ageGroups) ? obj.ageGroups : [];
          } else if (ra && typeof ra === 'object') {
            sexTypes = Array.isArray(ra?.sexTypes) ? ra.sexTypes : [];
            ageGroups = Array.isArray(ra?.ageGroups) ? ra.ageGroups : [];
          }
        } catch {}
        setGenders(sexTypes.map(String));
        setAges(ageGroups.map(String));

        // voucher (editable)
        setVoucherUsage(typeof v.usageType === 'string'
          ? v.usageType
          : (v.usageType == null ? '' : String(v.usageType)));
        setUseLimitByDay(v.useLimitByDay == null ? '' : String(v.useLimitByDay));
        setMaxPerUser(v.numberOfUsePerUser == null ? '' : String(v.numberOfUsePerUser));
      } catch (e) {
        if (e.name !== 'AbortError') setApiError(MSG.apiError);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [packageID]);

  // ---------- 画像アップロード ----------
  const handlePickClick = () => fileInputRef.current?.click();

  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (imagePreview && isBlobUrl(imagePreview)) {
      try { URL.revokeObjectURL(imagePreview); } catch {}
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    // serverImagePath は保存時に最終決定
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSingleImage(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const clearImage = () => {
    if (imagePreview && isBlobUrl(imagePreview)) {
      try { URL.revokeObjectURL(imagePreview); } catch {}
    }
    setImageFile(null);
    setImagePreview('');
    setServerImagePath(''); // 「削除」＝サーバー側も空（保存で反映）
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return null;
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name);
      fd.append('path', `coupon/${packageID}/`);
      const res = await fetch(`${API_BASE_URL}/v1/file`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: fd
      });
      if (!res.ok) {
        let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
        throw new Error(msg);
      }
      let resultPath = null;
      try { const j = await res.json(); resultPath = j?.path || j?.result || null; }
      catch { resultPath = await res.text(); }
      return resultPath;
    } finally {
      setUploading(false);
    }
  };

  // Submit (validate + PUT)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const salesVal = salesInputRef.current?.value?.trim() ?? '';
    const publishVal = publishInputRef.current?.value?.trim() ?? '';

    const newErrors = {};
    if (!packageName.trim()) newErrors.packageName = MSG.errPackageName;
    if (!spotId) newErrors.spotId = MSG.errSpot;
    if (publishedFlag !== true && publishedFlag !== false) newErrors.publishedFlag = MSG.errPublish;

    if (!salesVal) newErrors.salesRange = MSG.errSales;
    if (!publishVal) newErrors.publishRange = MSG.errPublishRange;

    if (voucherUsage !== '0' && voucherUsage !== '1') {
      newErrors.voucherUsage = MSG.errVoucherUsage;
    }
    const nPerUser = Number(maxPerUser);
    if (!Number.isInteger(nPerUser) || nPerUser < 0) {
      newErrors.numberOfUsePerUser = MSG.errNumberOfUsePerUser;
    }
    const nLimit = Number(useLimitByDay);
    if (!Number.isInteger(nLimit) || nLimit < 0) {
      newErrors.useLimitByDay = MSG.errUseLimitByDay;
    }

    setErrors(newErrors);
    const keys = Object.keys(newErrors);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const node = document.querySelector(`[data-error-key="${firstKey}"]`);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // 画像アップロード（必要なら）
    let uploadedImagePath = null;
    if (imageFile) {
      uploadedImagePath = await uploadImageIfNeeded();
      if (!uploadedImagePath) {
        alert('画像のアップロードに失敗しました。');
        return;
      }
    }

    // Build payload
    const token = localStorage.getItem('access_token') || '';
    const { start: salesStartDate, end: salesEndDate } = parseRangeToISO(salesVal);
    const { start: publishedStartDate, end: publishedEndDate } = parseRangeToISO(publishVal);

    // 最終的に送る画像パス（新規選択優先、なければ既存、削除なら空）
    const finalImagePath = uploadedImagePath ?? serverImagePath ?? '';

    const payload = {
      coupon: {
        packageName: packageName || '',
        spotId: spotId || '',
        displayOrder: displayOrder === '' ? null : Number(displayOrder),
        publishedFlag: !!publishedFlag,

        salesStartDate,
        salesEndDate,
        publishedStartDate,
        publishedEndDate,

        howToUse: howToUse || '',
        termsOfUse: termsOfUse || '',
        caution: caution || '',

        couponArea: ticketArea || '',
        couponGenre: genres.join(','),

        couponLevel: couponLevel === '' ? null : Number(couponLevel),
        usePoint: usePoint === '' ? null : Number(usePoint),
        maxNumber: maxNumber === '' ? null : Number(maxNumber),

        // 画像（サーバーに保存されるキー）
        couponImage: finalImagePath,
        imagePath: finalImagePath,

        recommendAttributes: {
          sexTypes: genders.map(String),
          ageGroups: ages.map(String)
        }
      },
      voucher: {
        voucherUsage,
        numberOfUsePerUser: Number(maxPerUser),
        useLimitByDay: Number(useLimitByDay)
      }
    };

    try {
      setSubmitting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/coupon/${packageID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.saved);
      navigate('/coupon');
    } catch (err) {
      console.error(err);
      setApiError(MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete (DELETE)
  const handleDelete = async () => {
    if (!window.confirm('このクーポンを削除します。よろしいですか？')) return;
    const token = localStorage.getItem('access_token') || '';
    try {
      setDeleting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/coupon/${packageID}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.deleted);
      navigate('/coupon');
    } catch (err) {
      console.error(err);
      setApiError(MSG.apiError);
    } finally {
      setDeleting(false);
    }
  };

  // class helpers
  const invalid = (key) => (errors[key] ? 'is-invalid' : '');
  const feedback = (key) => (errors[key] ? <div className="invalid-feedback d-block">{errors[key]}</div> : null);

  // options (optional UI parts)
  const areaOptions = useMemo(() => AREA_OPTIONS ?? [], []);
  const genreOptions = useMemo(() => TICKET_GENRE_OPTIONS ?? [], []);
  const genderOptions = useMemo(() => GENDER_OPTIONS ?? [], []);
  const ageOptions = useMemo(() => AGE_OPTIONS ?? [], []);

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/coupon">クーポン管理</a></li>
            <li className="breadcrumb-item active">編集</li>
          </ol>
        </nav>

        {apiError && <div className="alert alert-danger">{apiError}</div>}

        <form className="mb-9" onSubmit={handleSubmit} noValidate>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">クーポン編集</h2>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => window.history.back()}
                disabled={savingBusy}
              >
                キャンセル
              </button>
              <button
                className="btn btn-primary me-2 mb-2 mb-sm-0"
                type="submit"
                disabled={savingBusy}
              >
                {uploading ? '画像アップロード中…' : (submitting ? '更新中…' : '更新')}
              </button>
              <button
                className="btn btn-danger mb-2 mb-sm-0"
                type="button"
                onClick={handleDelete}
                disabled={savingBusy}
              >
                {deleting ? '削除中…' : '削除'}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">クーポンID</h4>
                    <input className="form-control" type="text" name="package_id" disabled defaultValue={packageID} />
                  </div>
                </div>
              </div>

              <div className="mb-4" data-error-key="packageName">
                <h4 className="mb-3">クーポン名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('packageName')}`}
                  type="text"
                  name="package_name"
                  value={packageName}
                  onChange={(e) => setCouponName(e.target.value)}
                  required
                  disabled={true}
                />
                {feedback('packageName')}
              </div>

              <div className="mb-4" data-error-key="spotId">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <div className="input-group">
                  <select
                    className={`form-control ${invalid('spotId')}`}
                    value={spotId}
                    onChange={(e) => setSpotId(e.target.value)}
                    disabled={true}
                    required
                  >
                    <option value="">{loadingSpots ? '読み込み中...' : '選択してください'}</option>
                    {spots.map((s) => (
                      <option key={s.spotId} value={s.spotId}>{s.spotName}</option>
                    ))}
                  </select>
                </div>
                {feedback('spotId')}
              </div>

              {/* テキスト */}
              <div className="mb-4">
                <h4 className="mb-3">使用方法</h4>
                <textarea className="form-control" name="how_to_use" rows={4} value={howToUse} onChange={(e) => setHowToUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">利用条件</h4>
                <textarea className="form-control" name="terms_of_use" rows={4} value={termsOfUse} onChange={(e) => setTermsOfUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">注意事項</h4>
                <textarea className="form-control" name="caution" rows={4} value={caution} onChange={(e) => setCaution(e.target.value)} />
              </div>

              <div className="row">
                <div className="mb-4 col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">必要ポイント</h4>
                    <input className="form-control" type="number" name="use_point" value={usePoint ?? ''} onChange={(e) => setUsePoint(e.target.value)} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">発行枚数</h4>
                    <input className="form-control" type="number" name="max_number" value={maxNumber ?? ''} onChange={(e) => setMaxNumber(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">クーポンレベル</h4>
                    <div className="d-flex align-items-center gap-3">
                      {['0','1','2','3'].map((lv) => (
                        <label className="mb-0" key={lv}>
                          <input
                            type="radio"
                            name="coupon_level"
                            value={lv}
                            checked={couponLevel === lv}
                            onChange={(e) => setCouponLevel(e.target.value)}
                          /> {lv}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="col-md-6" data-error-key="numberOfUsePerUser">
                  <div className="mb-4">
                    <h4 className="mb-3">1人利用可能回数<span className="text-danger">*</span></h4>
                    <input
                      className={`form-control ${invalid('numberOfUsePerUser')}`}
                      type="number"
                      name="number_of_use_per_user"
                      min={0}
                      step={1}
                      value={maxPerUser ?? ''}
                      onChange={(e) => setMaxPerUser(e.target.value)}
                      disabled={true}
                    />
                    {feedback('numberOfUsePerUser')}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6" data-error-key="voucherUsage">
                  <div className="mb-4">
                    <h4 className="mb-3">チケット利用区分<span className="text-danger">*</span></h4>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="voucher_usage"
                          value="0"
                          checked={voucherUsage === '0'}
                          onChange={(e) => setVoucherUsage(e.target.value)}
                          required
                          disabled={true}
                        /> 目視確認
                      </label>
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="voucher_usage"
                          value="1"
                          checked={voucherUsage === '1'}
                          onChange={(e) => setVoucherUsage(e.target.value)}
                          required
                          disabled={true}
                        /> QR表示
                      </label>
                    </div>
                    {feedback('voucherUsage')}
                  </div>
                </div>

                <div className="col-md-6" data-error-key="useLimitByDay">
                  <div className="mb-4">
                    <h4 className="mb-3">1日の使用上限回数<span className="text-danger">*</span></h4>
                    <input
                      className={`form-control ${invalid('useLimitByDay')}`}
                      type="number"
                      name="use_limit_by_day"
                      min={0}
                      step={1}
                      value={useLimitByDay ?? ''}
                      onChange={(e) => setUseLimitByDay(e.target.value)}
                      required
                      disabled={true}
                    />
                    {feedback('useLimitByDay')}
                  </div>
                </div>
              </div>

            </div>

            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12 col-sm-6 col-xl-12" data-error-key="publishedFlag">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="mb-4">
                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                        <div className="d-flex align-items-center gap-3">
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="0"
                              checked={publishedFlag === false}
                              onChange={() => setPublishedFlag(false)}
                              required
                            /> 非公開
                          </label>
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="1"
                              checked={publishedFlag === true}
                              onChange={() => setPublishedFlag(true)}
                              required
                            /> 公開
                          </label>
                        </div>
                        {feedback('publishedFlag')}
                      </div>

                      {/* 公開期間 */}
                      <div className="mb-4" data-error-key="publishRange">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                        </div>
                        <div className="input-group">
                          <input
                            id="publish_range"
                            className={`form-control ${invalid('publishRange')}`}
                            type="text"
                            name="publish_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={publishRange}
                            ref={publishInputRef}
                            required
                            readOnly
                            onChange={(e) => setPublishRange(e.target.value)}
                          />
                          <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            aria-label="クリア"
                            title="クリア"
                            onClick={() => setPublishRange('')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPublishRange(''); }}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >×</span>
                        </div>
                        {feedback('publishRange')}
                      </div>

                      {/* 有効期間 */}
                      <div className="mb-4" data-error-key="salesRange">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">有効期間<span className="text-danger">*</span></h5>
                        </div>
                        <div className="input-group">
                          <input
                            id="sales_range"
                            className={`form-control ${invalid('salesRange')}`}
                            type="text"
                            name="sales_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={salesRange}
                            ref={salesInputRef}
                            required
                            readOnly
                            onChange={(e) => setSalesRange(e.target.value)}
                            disabled={true}
                          />
                          {/* <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            aria-label="クリア"
                            title="クリア"
                            onClick={() => setSalesRange('')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSalesRange(''); }}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >×</span> */}
                        </div>
                        {feedback('salesRange')}
                      </div>

                      {/* 表示順 */}
                      <div className="mb-4">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                        </div>
                        <input
                          className="form-control"
                          type="number"
                          name="order"
                          value={displayOrder ?? ''}
                          onChange={(e) => setDisplayOrder(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 画像ブロック（SpotEdit と同等の挙動） */}
                <div className="col-12 col-sm-6 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-3">パッケージ画像</h4>

                      {/* プレビュー（サーバー画像 or 選択中のblob） */}
                      {imagePreview ? (
                        <div className="d-flex align-items-start gap-3 mb-3">
                          <img
                            src={imagePreview}
                            alt="プレビュー"
                            className="rounded border"
                            style={{ width: 160, height: 160, objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="d-flex flex-wrap gap-2">
                              <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingBusy}>
                                画像を変更
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingBusy}>
                                画像を削除
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // 画像が無い場合：ドロップ領域
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handlePickClick}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                          style={{ cursor: 'pointer' }}
                          aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                        >
                          <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                          <p className="mb-2">または</p>
                          <button type="button" className="btn btn-outline-primary" disabled={savingBusy}>ファイルを選択</button>
                          <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚）</p>
                        </div>
                      )}

                      {/* ファイル入力 */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={savingBusy}
                      />

                      {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                      {!loading && apiError && <div className="text-danger mt-3">{apiError}</div>}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right column ends */}
          </div>
        </form>
      </>
    </Layout>
  );
}
